<template>
  <SimpleModal
    v-if="transfer"
    :is-open="open"
    @close="close"
  >
    <div class="flex flex-col gap-4 px-7 py-10">
      <div class="flex items-center gap-3 text-gray-900 text-xl leading-6 font-semibold">
        <TransferIcon />
        <span> Receive Transfer #{{ transfer.reference_number }} </span>
      </div>

      <!-- HEADER -->
      <div class="gap-4 flex items-center">
        <div class="flex gap-3 items-center">
          <div
            class="font-semibold text-sm border-2 border-gray-400 rounded-lg px-4 bg-gray-300 h-[42px] flex items-center"
          >
            {{ selectedOrigin ? selectedOrigin.name : defaultLocationName }}
          </div>

          <ArrowRightCircleIcon class="w-6 h-6 text-gray-800" />

          <div class="flex">
            <div
              class="border-2 rounded-lg overflow-hidden relative flex items-center gap-1 h-[42px]"
              :class="{
                'error-field error-field-group border-red-400': getReceiveError('destination_space_id'),
                'border-gray-400': !getReceiveError('destination_space_id')
              }"
            >
              <div class="px-4 font-semibold text-sm">
                {{ selectedDestination?.name || defaultLocationName }}
              </div>

              <div class="horizontal-divider"></div>

              <select
                class="w-full border-none font-semibold focus:ring-0 py-0 h-[42px]"
                name="destination_space_id"
                id="destination_space_id"
                @change="
                  (event) => (receiveForm.destination_space_id = event.target.value ? Number(event.target.value) : null)
                "
              >
                <option hidden>{{ selectedDestinationSpace?.name || 'Select a Space' }}</option>
                <option value="">Select a Space</option>
                <option
                  v-for="space in destinationSpaces"
                  :key="space.id"
                  :value="space.id"
                >
                  {{ space.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <DatePicker
          v-model="receiveForm.received_at"
          color="blue"
          title-position="right"
          mode="dateTime"
        >
          <template v-slot="{ inputValue, inputEvents }">
            <div class="relative">
              <CalendarIcon
                class="top-3 left-4 h-5 w-5 text-gray-800 absolute"
                aria-hidden="true"
              />
              <input
                class="inline-flex items-center w-40 justify-center pl-10 border-2 border-gray-400 rounded-lg px-4 h-[42px] bg-gray-100 text-sm font-medium text-gray-800 cursor-pointer"
                :value="inputValue"
                v-on="inputEvents"
              />
            </div>
          </template>
        </DatePicker>
      </div>
      <!-- END HEADER -->

      <!-- START INITIAL COUNT -->
      <div v-if="!adjusting">
        <div class="divide-y divide-gray-400">
          <div class="flex items-center justify-between uppercase text-gray-600 text-xs font-semibold pb-4">
            <span>Packages</span>
            <span>Count</span>
          </div>

          <div
            v-for="(item, itemIndex) in receiveForm.packages"
            class="flex justify-between items-center gap-5"
            :class="{
              'py-4': itemIndex >= 0,
              'pb-0': itemIndex === receiveForm.packages.length - 1
            }"
          >
            <div class="text-left flex flex-col leading-tight">
              <span
                class="uppercase font-bold text-sm"
                :class="{ 'text-red-600': countError(item.id), 'text-blue-400': !countError(item.id) }"
              >
                {{ item.details.name }}
              </span>
              <span class="text-gray-800 text-sm font-medium">
                {{ item.details.tag_num }}
              </span>
            </div>
            <div class="text-center text-sm uppercase text-gray-600 font-semibold">
              <NumberStepper
                v-model="item.quantity"
                step="1"
                min="0"
                class="h-[36px] w-[70px] flex items-center justify-center border-2 rounded-lg px-4 select-none font-semibold text-sm text-gray-800"
                placeholder="0"
                :class="{
                  'border-red-600 text-red-600': countError(item.id),
                  'bg-gray-100 border-gray-400': !countError(item.id)
                }"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- END INITIAL COUNT -->

      <div
        v-if="!adjusting"
        class="border-t-2 border-blue-600"
      >
        <div class="flex items-center justify-between pt-4">
          <div class="font-semibold uppercase text-sm">Total Received</div>
          <div
            class="h-[36px] w-[70px] flex items-center justify-center border-2 border-gray-400 rounded-lg px-4 select-none font-semibold text-sm"
          >
            {{ totalQuantity }}
          </div>
        </div>
      </div>

      <!-- START ADJUSTMENTS -->
      <div v-if="adjusting">
        <div class="p-1 divide-y divide-gray-400">
          <div class="adjustment-grid grid grid-cols-6 gap-5 py-3">
            <div class="col-span-4 text-left text-xs uppercase text-gray-600 font-semibold">
              <span>Packages</span>
            </div>
            <div class="col-span-1 text-center text-xs uppercase text-gray-600 font-semibold">
              <span>Count</span>
            </div>
            <div class="col-span-1 text-center text-xs uppercase text-gray-600 font-semibold">
              <span>Actual</span>
            </div>
          </div>

          <div v-for="(item, packageIndex) in receiveForm.packages">
            <div class="adjustment-grid grid grid-cols-6 gap-5 py-4 items-center">
              <div class="col-span-2 text-left flex flex-col leading-tight">
                <span
                  class="uppercase font-bold text-sm"
                  :class="{ 'text-red-600': countError(item.id), 'text-blue-400': !countError(item.id) }"
                >
                  {{ item.details.name }}
                </span>
                <span class="text-gray-800 text-sm font-medium">
                  {{ item.details.batch_num }}
                </span>
              </div>
              <div class="col-span-2"></div>
              <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
                <div
                  class="h-[36px] w-[70px] flex items-center justify-center border-2 rounded-lg px-4 select-none font-semibold text-sm bg-gray-100"
                  :class="{
                    'border-red-600 text-red-600': countError(item.id),
                    'border-gray-400': !countError(item.id)
                  }"
                >
                  {{ item.quantity }}
                </div>
              </div>
              <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
                <div
                  class="h-[36px] w-[70px] flex items-center justify-center border-2 rounded-lg px-4 select-none font-semibold text-sm bg-gray-100"
                  :class="{
                    'border-red-600 text-red-600': countError(item.id),
                    'border-gray-400': !countError(item.id)
                  }"
                >
                  {{ getActual(item.id) }}
                </div>
              </div>
            </div>

            <!-- START ADJUSTMENT LIST -->
            <div v-if="countError(item.id) || (item.adjustments && item.adjustments.length > 0)">
              <div
                v-for="(adjustment, adjustmentIndex) in item.adjustments"
                class="adjustment-grid grid grid-cols-6 gap-5 py-1 items-center divide-none border-0 border-none"
                :class="{
                  'pb-8': receiveErrors?.[`packages.${packageIndex}.adjustments.${adjustmentIndex}.reason`]
                }"
              >
                <div class="col-span-4 flex items-center gap-2 relative">
                  <Menu
                    as="div"
                    class="relative text-left inline-block w-full"
                  >
                    <div>
                      <MenuButton
                        class="inline-flex items-center w-full justify-between rounded px-3 bg-gray-200 text-sm font-medium h-[36px]"
                        :class="[
                          receiveErrors?.[`packages.${packageIndex}.adjustments.${adjustmentIndex}.reason`]
                            ? 'border-red-400 border-2'
                            : 'border-gray-400 border'
                        ]"
                      >
                        {{ adjustment.reason || 'Reason' }}
                        <ChevronDownIcon
                          class="-mr-1 ml-2 h-4 w-4 text-gray-400"
                          aria-hidden="true"
                        />
                      </MenuButton>
                    </div>
                    <transition
                      enter-active-class="transition ease-out duration-100"
                      enter-from-class="opacity-0 scale-95"
                      enter-to-class="opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="opacity-100 scale-100"
                      leave-to-class="opacity-0 scale-95"
                    >
                      <MenuItems
                        class="justify-end origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg bg-gray-100 divide-y divide-gray-400 focus:outline-none z-50"
                      >
                        <div class="">
                          <MenuItem
                            v-slot="{ active }"
                            v-for="val in adjustmentReasons"
                          >
                            <a
                              @click="setAdjustmentReason(adjustment, val.name)"
                              href="#"
                              :class="[
                                active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100 text-left'
                              ]"
                            >
                              {{ val.name }}
                            </a>
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </transition>
                  </Menu>
                  <p
                    v-if="receiveErrors?.[`packages.${packageIndex}.adjustments.${adjustmentIndex}.reason`]"
                    class="-bottom-6 absolute font-medium left-0 text-red-400"
                  >
                    {{ receiveErrors?.[`packages.${packageIndex}.adjustments.${adjustmentIndex}.reason`] }}
                  </p>
                </div>
                <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
                  <NumberStepper
                    v-model="adjustment.amount"
                    step="1"
                    min="0"
                    class="h-[36px] w-[70px] flex items-center justify-center border-2 border-gray-400 rounded-lg px-4 select-none font-semibold text-sm text-gray-800"
                    placeholder="0"
                  />
                </div>
                <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
                  <button
                    type="button"
                    aria-label="Remove adjustment"
                    @click="removeAdjustment(item.id, adjustmentIndex)"
                  >
                    <MinusCircleIcon class="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>

              <div class="adjustment-grid grid grid-cols-6 gap-5 py-3 items-center divide-none border-0 border-none">
                <div class="col-span-2 text-left flex flex-col leading-tight"></div>
                <div class="col-span-2 flex items-center"></div>
                <div class="col-span-1 flex items-center"></div>
                <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
                  <button
                    type="button"
                    aria-label="Add adjustment"
                    @click="addAdjustment(item.id)"
                  >
                    <PlusCircleIcon class="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
            <!-- END ADJUSTMENT LIST -->
          </div>
        </div>

        <div class="border-t-2 border-blue-600 py-4">
          <div class="adjustment-grid grid grid-cols-6 gap-5 font-semibold">
            <div class="col-span-4 uppercase text-sm">Total Sent / Received</div>
            <div class="col-span-1">
              <span
                class="h-[36px] w-[70px] flex items-center justify-center border-2 border-gray-400 rounded-lg px-4 select-none font-semibold text-sm"
                >{{ totalActualQuantity }}</span
              >
            </div>
            <div class="col-span-1">
              <span
                class="h-[36px] w-[70px] flex items-center justify-center border-2 border-gray-400 rounded-lg px-4 select-none font-semibold text-sm"
                >{{ totalQuantity }}</span
              >
            </div>
          </div>
        </div>
      </div>
      <!-- END ADJUSTMENTS -->

      <div>
        <div v-if="!adjusting">
          <div v-if="!hasCountErrors">
            <div class="flex items-center justify-end">
              <div class="flex justify-end">
                <button
                  @click.prevent="submit()"
                  :disabled="receiving"
                  type="button"
                  class="btn btn-primary px-12 h-[42px] font-semibold uppercase"
                >
                  <spinner v-if="receiving"></spinner>
                  {{ submitText }}
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="hasCountErrors"
            class="bg-red-400 py-6 px-6 text-gray-100 rounded-b-lg -mx-7 -mb-12 flex items-center justify-between"
          >
            <div class="flex items-center gap-3 justify-between w-full">
              <div class="flex items-center gap-3">
                <ExclamationCircleIcon
                  class="h-7 w-7 text-gray-100"
                  aria-hidden="true"
                />
                <span class="text-gray-100 leading-snug block text-xs font-semibold"
                  >WARNING! The items highlighted above don’t match. <br />
                  Please recount or receive with explanation.</span
                >
              </div>

              <div class="flex gap-3">
                <button
                  @click.prevent="adjust(true)"
                  :disabled="receiving"
                  type="button"
                  class="btn px-3 h-[42px] text-gray-100 w-24 uppercase font-semibold bg-red-400 border-2 border-gray-100"
                >
                  <spinner v-if="receiving"></spinner>
                  {{ submitText }}
                </button>
                <button
                  @click.prevent="submit()"
                  :disabled="receiving"
                  type="button"
                  class="btn px-3 h-[42px] text-red-600 w-24 uppercase font-semibold"
                >
                  <spinner v-if="receiving"></spinner>
                  Recount
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="bg-blue-600 py-6 px-6 text-gray-100 rounded-b-lg -mx-7 -mb-12 flex items-center justify-between">
            <div class="flex items-center gap-3 justify-between w-full">
              <div class="flex items-center gap-3">
                <ExclamationCircleIcon
                  class="h-7 w-7 text-gray-100"
                  aria-hidden="true"
                />
                <span class="text-gray-100 leading-snug block text-xs font-semibold"
                  >CONFIRM! Please confirm receipt of this transfer.</span
                >
              </div>

              <div class="flex gap-3">
                <button
                  @click.prevent="submit()"
                  :disabled="creating"
                  type="button"
                  class="inline-flex items-center px-4 h-[42px] uppercase text-sm font-semibold rounded-md bg-gray-100 text-gray-800"
                >
                  <spinner v-if="creating"></spinner>
                  Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SimpleModal>
</template>

<style scoped>
.horizontal-divider {
  height: calc(100% - 20px);
  width: 2px;
  @apply bg-gray-500;
}
.adjustment-grid {
  grid-template-columns: repeat(4, 1fr) minmax(0, 75px) minmax(0, 75px);
}
</style>

<script setup>
import { ArrowRightCircleIcon } from '@heroicons-2/vue/24/outline'
import NumberStepper from '@/Components/Forms/NumberStepper.vue'
import TransferIcon from '@/Components/Icons/TransferIcon.vue'
</script>

<script>
import SimpleModal from '@/Components/Modals/SimpleModal.vue'
import Spinner from '@/Components/Icons/Spinner.vue'
import { XCircleIcon, SearchIcon, ChevronDownIcon } from '@heroicons/vue/solid'
import { PlusCircleIcon, MinusCircleIcon, CalendarIcon, ExclamationCircleIcon } from '@heroicons/vue/outline'
import getsErrors from '@/Mixins/getsErrors'
import { Calendar, DatePicker } from 'v-calendar'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import receivesTransfer from '@/Pages/Transfers/Mixins/receivesTransfer'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import accessesCompany from '@/Mixins/accessesCompany'

export default {
  mixins: [getsErrors, receivesTransfer, interactsWithCurrentLocation, accessesCompany],

  components: {
    SimpleModal,
    Spinner,
    XCircleIcon,
    SearchIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    ChevronDownIcon,
    CalendarIcon,
    ExclamationCircleIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Calendar,
    DatePicker
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    },
    adjustmentReasons: {
      type: Array,
      default() {
        return []
      }
    }
  },

  emits: ['update:open', 'adjust', 'received'],

  data() {
    return {
      adjusting: false,
      receiving: false
    }
  },

  computed: {
    submitText() {
      if (this.creating) {
        return 'Saving...'
      }

      return this.actionText
    }
  },

  methods: {
    close() {
      this.$emit('update:open', false)
      this.adjusting = false
    },
    emitReceived() {
      this.$emit('received')
    },
    submit() {
      if (this.receiving) {
        return
      }

      this.receiving = true
      this.receive(
        () => {
          this.receiveForm.reset()
          this.close()
          this.emitReceived()
        },
        null,
        () => {
          this.receiving = false
        }
      )
    },
    adjust(shouldAdjust = true) {
      this.adjusting = shouldAdjust
    },
    getActual(packageId) {
      const packages = this.transfer.packages || []
      const pkg = packages.find((pkg) => pkg.id === packageId)

      return pkg?.quantity
    },
    addAdjustment(packageId) {
      const packages = this.receiveForm.packages || []
      const pkg = packages.find((pkg) => pkg.id === packageId)

      pkg.adjustments = pkg.adjustments || []

      pkg.adjustments.push({
        amount: 0,
        reason: ''
      })
    },
    removeAdjustment(packageId, adjustmentIndex) {
      const packages = this.receiveForm.packages || []
      const pkg = packages.find((pkg) => pkg.id === packageId)

      pkg.adjustments.splice(adjustmentIndex, 1)
    },
    setAdjustmentReason(adjustment, reason) {
      adjustment.reason = reason
    }
  }
}
</script>
